import { AppService } from './../state/app/app.service';
import { SessionStorageType } from './tracking/session-storage';
import { Injectable } from '@angular/core';
import { CookieOptions, CookieService } from 'ngx-cookie';
import { WindowRef } from './windowRef.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EnvironmentService } from './environment.service';
import { CookieName } from '../constants/product-domain';
import { createAppState } from '../state/app';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {
  public isUSCustomer: boolean | null = null;

  constructor(
    private cookieService: CookieService,
    private windowService: WindowRef,
    private http: HttpClient,
    private env: EnvironmentService,
    private appService: AppService
  ) { }

  getGeolocationData(): Observable<any> {
    const url = this.env.getGeoLocationUrl();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      accept: 'application/json',
    });

    return this.http.get(url, { headers });
  }

  requestIsUSCustomer(): Observable<boolean> {
    return this.getGeolocationData().pipe(
      map(
        (response: any) => {
          if (response !== null) {
            this.isUSCustomer = response.country_code.toLowerCase() === 'us';
          } else {
            // If the response is null (likely because lower environments return a blank response)
            // assume the customer is a US customer and proceed to the gsuite page
            this.isUSCustomer = true;
          }
          return this.isUSCustomer;
        }
      )
    );
  }

  private getExpiryDate(days: number): string {
    const currentDate = new Date();
    currentDate.setTime(currentDate.getTime() + (1000 * 60 * 60 * 24 * days)); // adds days in milliseconds
    return currentDate.toUTCString();
  }

  setApprovedCookie(cookieType: string, name: string, value: string, storeUnencoded: boolean = false, expirationInDays: number = 365): void {
    const nativeWin = this.windowService.nativeWindow;
    const approvedCookieCats: string[] = nativeWin.OptanonActiveGroups ? nativeWin.OptanonActiveGroups.split(',') : ['C0001'];
    const cookieDomain = this.windowService.nativeWindow.location.hostname === 'localhost' ? 'localhost' : 'legalzoom.com';
    const cookieOptions: CookieOptions = { domain: cookieDomain, expires: this.getExpiryDate(expirationInDays), storeUnencoded };
    if (approvedCookieCats.indexOf(cookieType) !== -1) {
      this.cookieService.put(name, value, cookieOptions);
    }
  }

  getStringParamFromQueryString(paramName): string {
    return this.getParamValueQueryString(paramName) ? this.getParamValueQueryString(paramName) : undefined;
  }

  getIntParamFromQueryString(paramName): number {
    return (this.getParamValueQueryString(paramName) &&
          parseInt(this.getParamValueQueryString(paramName), 10)) ?
      Number(this.getParamValueQueryString(paramName)) : undefined;
  }

  getParamValueQueryString(paramName): string {
    const url = window.location.href;
    let paramValue;
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      paramValue = httpParams.get(paramName);
    }
    return paramValue;
  }

  clearCookies(): void {
    const cookieDomain = this.windowService.nativeWindow.location.hostname === 'localhost' ? 'localhost' : 'legalzoom.com';
    const cookieOptions: CookieOptions = { domain: cookieDomain };
    this.cookieService.remove(CookieName.LZAuthCookie, cookieOptions);
    this.cookieService.remove(CookieName.USERIDENTIFICATION, cookieOptions);
    this.cookieService.remove(CookieName.TIMESTRING1, cookieOptions);
  }

  clearAppState(removeCookies: boolean): void {
    if (removeCookies) {
      this.clearCookies();
    }
    this.windowService.nativeWindow.sessionStorage.removeItem(SessionStorageType.APP);
    this.appService.app = createAppState({});
  }

  getAuthTokenConsultationCookie() {
    const userIdCookieValue = this.cookieService.get(CookieName.USERIDENTIFICATION);
    let authToken = '';
    if (userIdCookieValue) {
      const userIdCookieValues: string[] = userIdCookieValue.split('&');
      userIdCookieValues.forEach((value: string) => {
        if (value.startsWith('consultation_id=')) {
          authToken = value.replace('consultation_id=', '');
        }
      });
    }
    return authToken;
  }

  getCustomerIdConsultationCookie() {
    const userIdCookieValue = this.cookieService.get(CookieName.USERIDENTIFICATION);
    let customerId = 0;
    if (userIdCookieValue) {
      const userIdCookieValues: string[] = userIdCookieValue.split('&');
      userIdCookieValues.forEach((value: string) => {
        if (value.startsWith('cid=')) {
          customerId = +value.replace('cid=', '');
        }
      });
    }
    return customerId;
  }
}
